import { useEffect, useMemo, useRef, useState } from "react";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Search, Users, Pencil, Trash2, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "../contexts/AuthContext";
import { AppPagination, PaginationInfo } from "@/components/ui/app-pagination";
import { ServerTable } from "@/components/server-table";

const COL = {
  first: "w-[18%] min-w-[160px]",
  last: "w-[18%] min-w-[160px]",
  email: "w-[34%] min-w-[300px]",
  role: "w-[12%] min-w-[110px]",
  actions: "w-[18%] min-w-[160px]",
};

type UserRow = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  createdAt: string;
  updatedAt: string;
};

const UsersTab = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName,  setLastName]  = useState("");
  const [email,     setEmail]     = useState("");
  const [password,  setPassword]  = useState("");
  const [role,      setRole]      = useState("");

  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const [deleteUser,   setDeleteUser]   = useState<UserRow | null>(null);
  const [isSaving,     setIsSaving]     = useState(false);

  // server-backed data
  const [rows, setRows] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });

  const { toast } = useToast();
  const { user } = useAuth();

  // Debounce search
  const debounceRef = useRef<number | undefined>(undefined);
  const setSearchQuery = (val: string) => {
    setQ(val);
    // reset page on new query
    setPage(1);
  };
  const debouncedQ = useMemo(() => q, [q]); // we’ll debounce in effect below

  useEffect(() => {
    if (user?.role !== "ADMIN") return;
    // debounce 400ms
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      fetchUsers(page, debouncedQ);
    }, 400);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, page, debouncedQ]);
 const fetchUsers = async (pageParam: number, qParam: string, pageSizeOverride?: number) => {
  try {
    setLoading(true);
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) throw new Error("No access token found.");

    const url = new URL(`${import.meta.env.VITE_API_URL}/auth/users`);
    url.searchParams.set("page", String(pageParam));
    url.searchParams.set("limit", String(pageSizeOverride ?? pagination.pageSize));
    if (qParam?.trim()) url.searchParams.set("q", qParam.trim());

    const res = await fetch(url.toString(), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.error || "Failed to fetch users.");
    }

    const json = await res.json();
    const items: UserRow[] = json?.data ?? [];
    const meta = json?.meta ?? {};

    setRows(items);
    setPagination({
      page: meta.page ?? pageParam,
      pageSize: meta.pageSize ?? (pageSizeOverride ?? 10),
      total: meta.total ?? items.length,
      totalPages: meta.totalPages ?? 1,
      hasNext: !!meta.hasNext,
      hasPrev: !!meta.hasPrev,
    });
  } catch (err) {
    console.error(err);
    toast({
      title: "Fetch Failed",
      description: err instanceof Error ? err.message : "Failed to load users.",
      variant: "destructive",
    });
  } finally {
    setLoading(false);
  }
};


  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPassword("");
    setRole("");
    setIsEditMode(false);
    setSelectedUser(null);
  };

  const openEditDialog = (u: UserRow) => {
    setIsEditMode(true);
    setSelectedUser(u);
    setFirstName(u.firstName);
    setLastName(u.lastName);
    setEmail(u.email);
    setRole(u.role);
    setPassword("");
    setIsDialogOpen(true);
  };

  const openDeleteConfirm = (u: UserRow) => {
    setDeleteUser(u);
    setIsDeleteOpen(true);
  };

  const handleSaveUser = async () => {
    if (!firstName || !lastName || !email || !role || (!isEditMode && !password)) {
      toast({
        title: "Validation Error",
        description: "All required fields must be filled.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSaving(true);
      const accessToken = localStorage.getItem("accessToken");
      if (!accessToken) throw new Error("No access token found.");

      let url = `${import.meta.env.VITE_API_URL}/auth/create-users`;
      let method = "POST";
      const body: any = {
        firstName, lastName, email, role,
        organizationId: user?.organizationId,
      };

      if (isEditMode && selectedUser) {
        url = `${import.meta.env.VITE_API_URL}/auth/users/${selectedUser.id}`;
        method = "PUT";
        delete body.email;
        delete body.organizationId;
        delete body.password;
        if (selectedUser.id === user?.id) delete body.role;
      } else {
        body.password = password;
      }

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `Failed to ${isEditMode ? "update" : "create"} user.`);
      }

      toast({
        title: `User ${isEditMode ? "Updated" : "Created"}`,
        description: `The user has been ${isEditMode ? "updated" : "added"} successfully.`,
      });

      resetForm();
      setIsDialogOpen(false);
      // refetch current page with current query
      fetchUsers(page, q);
    } catch (err) {
      console.error(err);
      toast({
        title: "Operation Failed",
        description: err instanceof Error ? err.message : `Failed to ${isEditMode ? "update" : "create"} user.`,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUser) return;

    try {
      setIsSaving(true);
      const accessToken = localStorage.getItem("accessToken");
      if (!accessToken) throw new Error("No access token found.");

      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/auth/users/${deleteUser.id}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${accessToken}` } }
      );

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || "Failed to delete user.");
      }

      toast({ title: "User Deleted", description: "The user has been deleted successfully." });
      setIsDeleteOpen(false);
      setDeleteUser(null);

      // If we deleted the last item of the page, bounce back one page (classic UX)
      const isLastItemOnPage = rows.length === 1 && page > 1;
      const nextPage = isLastItemOnPage ? page - 1 : page;
      setPage(nextPage);
      fetchUsers(nextPage, q);
    } catch (err) {
      console.error(err);
      toast({
        title: "Deletion Failed",
        description: err instanceof Error ? err.message : "Failed to delete user.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Non-admin fallback
  if (user?.role !== "ADMIN") {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-light">
            <Users className="w-5 h-5" />
            Users
          </CardTitle>
          <CardDescription className="font-light">
            Access restricted to administrators.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const columns = [
    { key: "firstName", header: "First Name", className: `${COL.first}`, render: (r: UserRow) => (
      <div className="truncate" title={r.firstName}>{r.firstName}</div>
    )},
    { key: "lastName",  header: "Last Name",  className: `${COL.last}`, render: (r: UserRow) => (
      <div className="truncate" title={r.lastName}>{r.lastName}</div>
    )},
    { key: "email",     header: "Email",      className: `${COL.email}`, render: (r: UserRow) => (
      <div className="truncate" title={r.email}>{r.email}</div>
    )},
    { key: "role",      header: "Role",       className: `${COL.role}`, render: (r: UserRow) => (
      <div className="truncate">{r.role.replace("_", " ")}</div>
    )},
    { key: "actions",   header: <div className="text-center">Actions</div>, className: `${COL.actions}`, render: (r: UserRow) => {
      const hideDelete = r.role === "ADMIN" || r.id === user?.id;
      return (
        <div className="flex items-center justify-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => openEditDialog(r)} className="font-medium">
            <Pencil className="w-4 h-4 mr-1" /> Edit
          </Button>
          {!hideDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openDeleteConfirm(r)}
              className="font-medium text-destructive"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
          )}
        </div>
      );
    }},
  ];

  return (
    <Card className="border border-border/60 shadow-sm">
      <CardHeader className="pb-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 font-light">
              <Users className="w-5 h-5" />
              Users
            </CardTitle>
            <CardDescription className="font-light">
              Manage users in your organization.
            </CardDescription>
          </div>

          {/* Add / Edit dialog opener */}
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="font-medium"
                onClick={() => {
                  setIsEditMode(false);
                  resetForm();
                }}
              >
                Add New User
              </Button>
            </DialogTrigger>

            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-light">
                  {isEditMode ? "Edit User" : "Add New User"}
                </DialogTitle>
                <DialogDescription className="font-light">
                  {isEditMode ? "Update the user details." : "Enter the details for the new user."}{" "}
                  Organization is set to your current one.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="font-light">First Name</Label>
                    <Input id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Enter first name" className="font-light" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="font-light">Last Name</Label>
                    <Input id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Enter last name" className="font-light" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="font-light">Email</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter email" className="font-light" disabled={isEditMode} />
                </div>

                {!isEditMode && (
                  <div className="space-y-2">
                    <Label htmlFor="password" className="font-light">Password</Label>
                    <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter password" className="font-light" />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="role" className="font-light">Role</Label>
                  <select
                    id="role"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className="w-full border rounded-md px-3 py-2 bg-background"
                    disabled={isEditMode && selectedUser?.id === user?.id}
                  >
                    <option value="" disabled>Select role</option>
                    <option value="ANALYST">Analyst</option>
                    <option value="REVIEWER">Reviewer</option>
                    <option value="VIEWER">Viewer</option>
                  </select>
                </div>
              </div>

              <DialogFooter>
                <Button onClick={handleSaveUser} disabled={isSaving} className="font-medium">
                  {isSaving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>) : (isEditMode ? "Update User" : "Create User")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Search (server-side, debounced) */}
        <div className="relative">
          <Input
            placeholder="Search by name, email, or role..."
            value={q}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 font-light"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        </div>

        {/* Table (no local filtering/pagination) */}
        <ServerTable<UserRow>
          columns={[
            ...[
              { key: "firstName", header: "First Name", className: `${COL.first}`, render: (r: UserRow) => <div className="truncate" title={r.firstName}>{r.firstName}</div> },
              { key: "lastName",  header: "Last Name",  className: `${COL.last}`,  render: (r: UserRow) => <div className="truncate" title={r.lastName}>{r.lastName}</div> },
              { key: "email",     header: "Email",      className: `${COL.email}`, render: (r: UserRow) => <div className="truncate" title={r.email}>{r.email}</div> },
              { key: "role",      header: "Role",       className: `${COL.role}`,  render: (r: UserRow) => <div className="truncate">{r.role.replace("_", " ")}</div> },
            ],
            {
              key: "actions",
              header: <div className="text-center">Actions</div>,
              className: `${COL.actions}`,
              render: (r: UserRow) => {
                const hideDelete = r.role === "ADMIN" || r.id === user?.id;
                return (
                  <div className="flex items-center justify-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => openEditDialog(r)} className="font-medium">
                      <Pencil className="w-4 h-4 mr-1" /> Edit
                    </Button>
                    {!hideDelete && (
                      <Button variant="ghost" size="sm" onClick={() => openDeleteConfirm(r)} className="font-medium text-destructive">
                        <Trash2 className="w-4 h-4 mr-1" /> Delete
                      </Button>
                    )}
                  </div>
                );
              },
            },
          ]}
          data={rows}
          loading={loading}
          emptyMessage="No users found."
          rowKey={(r) => r.id}
        />

        {/* Pagination (chip-style buttons) */}
        <AppPagination
          pagination={pagination}
          onPageChange={(p) => setPage(p)}
          onPageSizeChange={(size) => {
          setPagination((prev) => ({ ...prev, pageSize: size }));
          setPage(1); // reset to first page
          fetchUsers(1, q, size);
  }}
  showPageSize={true}
  pageSizeOptions={[10, 20, 50]}
  maxPageButtons={7}
/>

      </CardContent>

      {/* Delete dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-light">Confirm Deletion</DialogTitle>
            <DialogDescription className="font-light">
              Are you sure you want to delete {deleteUser?.firstName} {deleteUser?.lastName}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteOpen(false)} className="font-medium">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteUser} disabled={isSaving} className="font-medium">
              {isSaving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting...</>) : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default UsersTab;
